import {
  isPasswordValidator,
  isNotEmptyTrimmedStringValidator,
  isEmailValidator,
  isValueValidator,
} from '@currency-one/validators'
import { t9n } from 't9n/t9n'

import { RegisterPayload } from 'models/endpoints-payload'
import { RegisterResponse } from 'models/endpoints-respone'
import { AllowedFunctionalCookiesForPanelArray } from 'models/cookies'

import { pushDataLayer } from 'utils/data-layer-push'
import { getCurrentLanguage } from 'utils/current-language'
import { getApiErrorMessage, getGenericErrorMessage, isApiError } from 'utils/error-message'
import { createUrlWithParams } from 'utils/urls'

import { registerUser } from 'services/register-user'

import { collectDataToSend, getElementsForm } from './form-handling-competition'

type FormContainer = Document | HTMLElement
type StringValidator = (value: string, selector: string, formContainer: FormContainer) => string
type BooleanValidator = (value: boolean, selector: string, formContainer: FormContainer) => boolean

declare const USER_BASE_URL_WT: string
const currentLanguage = getCurrentLanguage()
const SPACE_REGEX = / /g
const boundControls = new Set<Element>()
const FORM_FIELD_SELECTOR = '.general-form-style__single-field'

// Function reset all error spans for form and remove is-invalid class from inputs.
export const resetAllErrorMSG = ($formContainer: FormContainer): void => {
  const $errorMsgsSpans = $formContainer.querySelectorAll<HTMLSpanElement>('[data-error-field]')
  const $invalidInputs = $formContainer.querySelectorAll<HTMLInputElement>('.is-invalid')
  const $serverError = $formContainer.querySelector<HTMLParagraphElement>('[data-error-server]')

  $errorMsgsSpans.forEach(($singleErrorSpan) => {
    $singleErrorSpan.hidden = true
  })

  $invalidInputs.forEach(($singleInvalidField) => {
    $singleInvalidField.classList.remove('is-invalid')
  })

  if ($serverError) $serverError.hidden = true
}

// Function set error MSG for span and highlight invalid form field.
export const setInputErrorMSG = (selector: string, errorMSG: string, $formContainer: FormContainer): void => {
  const $inputWithError = $formContainer.querySelector<HTMLElement>(selector)

  if (!$inputWithError) {
    console.warn(`Element with selector "${selector}" not found in the form container.`)
    return
  }

  const $fieldContainer = $inputWithError.closest(FORM_FIELD_SELECTOR)

  if (!$fieldContainer) {
    console.warn(`Could not find a parent container "${FORM_FIELD_SELECTOR}" for selector "${selector}".`)
    return
  }

  const $errorSpanElement = $fieldContainer.querySelector<HTMLSpanElement>('[data-error-field]')

  if ($errorSpanElement) {
    $errorSpanElement.innerHTML = errorMSG
    $errorSpanElement.hidden = false
  } else {
    console.warn(`Error field [data-error-field] not found within the container for selector "${selector}".`)
  }

  $inputWithError.classList.add('is-invalid')
}

// Function set error MSG for span and highlight invalid form field.
export const setErrorMSG = (selector: string, errorMSG: string, $formContainer: FormContainer): void => {
  const $errorLabel = $formContainer.querySelector<HTMLElement>(selector)

  if ($errorLabel) {
    $errorLabel.innerHTML = errorMSG
    $errorLabel.hidden = false
  }
}

/**
 * Function checks if array of test is empty. If not set error label based on detected error
 * and add is-invalid class for input.
 */
export const checkValidity = (
  checkResults: Array<string>,
  selector: string,
  $formContainer: FormContainer,
): boolean => {
  checkResults = checkResults.filter((n) => n)
  if (checkResults.length) {
    setInputErrorMSG(selector, checkResults[0], $formContainer)
    return false
  }
  return true
}

// Validator for email input field.
export const emailValidator = (value: string, selector: string, $formContainer: FormContainer): string | null => {
  const isValid = [
    !isNotEmptyTrimmedStringValidator(value) ? t9n('formValidation.emptyField') : '',
    !isEmailValidator(value) ? t9n('formValidation.emailNotValid') : '',
  ]
  return checkValidity(isValid, selector, $formContainer) ? value : null
}

// Validator for password input field
export const passwordValidator = (value: string, selector: string, $formContainer: FormContainer): string | null => {
  const isValid = [
    !isNotEmptyTrimmedStringValidator(value) ? t9n('formValidation.emptyField') : '',
    !isPasswordValidator(value) ? t9n('formValidation.passwordToShort') : '',
  ]
  return checkValidity(isValid, selector, $formContainer) ? value : null
}

// Validator for checkbox input field.
export const consentValidator = (value: boolean, selector: string, $formContainer: FormContainer): boolean | null => {
  const isValid = [!isValueValidator(value) ? t9n('formValidation.requiredField') : '']
  return checkValidity(isValid, selector, $formContainer) ? value : null
}

// Function retrieves and validate the value of an input field.
export const getStringField = (
  selector: string,
  validator: StringValidator,
  $formContainer: FormContainer,
  shouldBeValidated = true,
): string | null => {
  const ALLOWED_INPUT_TYPES = ['text', 'hidden', 'password', 'textarea']
  const $inputElement = $formContainer.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector)

  if (!$inputElement || !ALLOWED_INPUT_TYPES.includes($inputElement.type)) return null

  const value = String($inputElement.value).trim()
  return shouldBeValidated ? validator(value, selector, $formContainer) : value
}

export const getBooleanFieldCompat = (
  selector: string,
  validator: BooleanValidator,
  $formContainer: FormContainer,
  shouldBeValidated = true,
  fallbackWhenMissing = true,
): boolean => {
  const ALLOWED_INPUT_TYPES = ['checkbox']
  const $inputElement = $formContainer.querySelector<HTMLInputElement>(selector)

  if (!$inputElement || !ALLOWED_INPUT_TYPES.includes($inputElement.type)) {
    return fallbackWhenMissing
  }

  const value = $inputElement.checked
  return shouldBeValidated ? validator(value, selector, $formContainer) : value
}

// Function retrieves and validate the value of an checkbox field.
export const getBooleanField = (
  selector: string,
  validator: BooleanValidator,
  $formContainer: FormContainer,
  shouldBeValidated = true,
): boolean | null => {
  const ALLOWED_INPUT_TYPES = ['checkbox']
  const $inputElement = $formContainer.querySelector<HTMLInputElement>(selector)

  if (!$inputElement || !ALLOWED_INPUT_TYPES.includes($inputElement.type)) return null

  const value = $inputElement.checked
  return shouldBeValidated ? validator(value, selector, $formContainer) : value
}

// Function retrieves and validate the value of an promocode field.
export const getPromoCodeField = ($formContainer: FormContainer): string => {
  const $inputElement = $formContainer.querySelector<HTMLInputElement>('[data-promocode-field]')
  return $inputElement ? $inputElement.value.trim() : ''
}

// Function fetches data from form and create payload object.
export const getFormData = ($formContainer: FormContainer): Partial<RegisterPayload> => {
  return {
    email: getStringField('[data-email-field]', emailValidator, $formContainer),
    password: getStringField('[data-password-field]', passwordValidator, $formContainer),
    promocode: getPromoCodeField($formContainer),
    terms_and_conditions_accepted: getBooleanField('[data-checkbox-field="terms"]', consentValidator, $formContainer),
    promotion_program_rules_accepted:
      getBooleanField('[data-checkbox-field="promotion"]', consentValidator, $formContainer) || undefined,
    marketing_email_actions_accepted:
      getBooleanField('[data-checkbox-field="marketing-consent"]', consentValidator, $formContainer, false) ||
      undefined,
    region: 'PL', // deprecated stuff - long history of WT
    language: currentLanguage,
  }
}

/**
 * Checks if the payload contains all required fields with valid (not null and not undefined) values.
 * Optional fields in the RegisterPayload type are ignored.
 */
export const checkIfPayloadComplete = (payload: Partial<RegisterPayload>): payload is RegisterPayload => {
  const REQUIRED_FIELDS: ReadonlyArray<keyof RegisterPayload> = [
    'email',
    'password',
    'promocode',
    'terms_and_conditions_accepted',
    'region',
    'language',
  ]

  const requiredFieldsValid = REQUIRED_FIELDS.every((field) => {
    const value = payload[field]

    if (value == null) return false

    switch (field) {
      case 'email':
      case 'password':
      case 'region':
      case 'language':
        return typeof value === 'string' && value.trim() !== ''
      case 'promocode':
        return typeof value === 'string' // promocode can be empty string
      case 'terms_and_conditions_accepted':
        return value === true
      default:
        return true // For any other fields, just check for non-null/undefined
    }
  })

  // If promotion_program_rules_accepted exists and is not null/undefined,
  // not all forms have promotions
  if ('promotion_program_rules_accepted' in payload) {
    const value = payload.promotion_program_rules_accepted
    const isValid = value != null && value === true
    return isValid && requiredFieldsValid
  }

  return requiredFieldsValid
}

const handleFormSubmit = async (
  $formContainer: FormContainer = document,
  isWithCompetition: boolean = false,
): Promise<void> => {
  resetAllErrorMSG($formContainer)

  const captchaToken = '' // Placeholder for captcha token, replace with actual token retrieval logic
  const payload: RegisterPayload = {
    ...(getFormData($formContainer) as RegisterPayload),
    captcha: captchaToken,
  }

  if (!checkIfPayloadComplete(payload)) {
    console.warn('Incomplete or invalid form data')
    return
  }

  try {
    const response = await registerUser(payload)
    handleSuccessfulRegistration(payload, response, isWithCompetition)
  } catch (error: unknown) {
    handleFailedRegistration(error, $formContainer)
  }
}

export const createConfirmEmailUrl = (domain: string, params: AllowedFunctionalCookiesForPanelArray): string => {
  const redirect = `${domain}/#/confirm-email`
  const createdUrl = createUrlWithParams(params, new URL(redirect))

  return createdUrl
}

const handleSuccessfulRegistration = (
  payload: RegisterPayload,
  response: RegisterResponse,
  isWithCompetition: boolean,
): void => {
  const language = payload.language || getCurrentLanguage()
  const registrationRef = response.ref || ''

  pushDataLayer('form_submitted', { form_name: 'Rejstracja z landingu', form_url: window.location.href })

  if (isWithCompetition) {
    const competitionFrom = getElementsForm()
    collectDataToSend(competitionFrom)
  }

  window.location.href = createConfirmEmailUrl(USER_BASE_URL_WT, { language, registrationRef })
}

export const handleFailedRegistration = (error: unknown, $formContainer: FormContainer): void => {
  if (isApiError(error)) {
    const message = getApiErrorMessage(error)

    switch (message) {
      case 'email-already-exists':
      case 'biz-email-already-exists':
        setInputErrorMSG('[data-email-field]', t9n('formValidation.emailReserved'), $formContainer)
        return
      case 'promocode-invalid':
        setErrorMSG('[data-error-server]', t9n('formValidation.promocodeInvalid'), $formContainer)
        return
      default:
        console.error('Registration error:', message)
        setErrorMSG('[data-error-server]', t9n('formValidation.serverUnknownError'), $formContainer)
    }
  } else {
    const message = getGenericErrorMessage(error)
    console.error('Generic Error on submit:', error)
    setErrorMSG('[data-error-server]', message, $formContainer)
  }
}

const handleShowPassword = ($showPasswordButton: HTMLButtonElement, $passwordInput: HTMLInputElement): void => {
  if ($passwordInput) {
    $passwordInput.type = `${$passwordInput.type === 'text' ? 'password' : 'text'}`
    $showPasswordButton.textContent = `${$passwordInput.type === 'text' ? t9n('password.hide') : t9n('password.show')}`
  }
}

const removeSpaces = (value: string): string => value.replace(SPACE_REGEX, '')

const setValueKeepingCursor = ($input: HTMLInputElement, transform: (value: string) => string): void => {
  const cursorPosition = $input.selectionStart ?? $input.value.length
  const cursorPositionAfterTransform = transform($input.value.slice(0, cursorPosition)).length

  $input.value = transform($input.value)
  $input.setSelectionRange(cursorPositionAfterTransform, cursorPositionAfterTransform)
}

export const handlePasswordInput = ($passwordInput: HTMLInputElement): void => {
  const { value } = $passwordInput

  if (value === removeSpaces(value)) return

  setValueKeepingCursor($passwordInput, removeSpaces)
}

export const bindPasswordControls = ($formContainer: FormContainer): void => {
  $formContainer.querySelectorAll<HTMLInputElement>('[data-password-field]').forEach(($passwordInput) => {
    if (boundControls.has($passwordInput)) return
    boundControls.add($passwordInput)

    const $showPasswordButton = $passwordInput
      .closest(FORM_FIELD_SELECTOR)
      ?.querySelector<HTMLButtonElement>('[data-show-password-button]')

    $passwordInput.addEventListener('input', () => handlePasswordInput($passwordInput))

    if ($showPasswordButton)
      $showPasswordButton.addEventListener('click', () => handleShowPassword($showPasswordButton, $passwordInput))
  })
}

export const bindSubmitButton = ($formContainer: FormContainer, onSubmit: () => void): void => {
  const $submitButton = $formContainer.querySelector<HTMLButtonElement>('[data-submit-form]')

  if (!$submitButton || boundControls.has($submitButton)) return

  boundControls.add($submitButton)
  $submitButton.addEventListener('click', onSubmit)
}

const bindFormControls = ($formContainer: FormContainer, isWithCompetition: boolean = false): void => {
  bindSubmitButton($formContainer, () => handleFormSubmit($formContainer, isWithCompetition))
  bindPasswordControls($formContainer)
}

export const initForm = (isWithCompetition: boolean = false): void => {
  bindFormControls(document, isWithCompetition)
}

export const initExitPopupFormControls = (): void => {
  const $form = document.querySelector<HTMLElement>('[data-exit-popup]')
  if ($form) bindFormControls($form)
}
